import React, { useState, useEffect, useCallback, lazy, Suspense, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import useCapabilities from '../hooks/useCapabilities';
import { useCache } from '../contexts/CacheContext';
import { analyticsAPI, adminAPI, userAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';
import { 
  Users, 
  Calendar,
  CalendarCheck,
  CalendarClock,
  TrendingUp, 
  Activity, 
  CheckCircle, 
  Clock, 
  MapPin, 
  
  RefreshCw,
  DollarSign,
  MessageSquare,
  
  
  Phone,
  LifeBuoy,
  TimerReset,
  // Same icon the sidebar uses for FAQs, so the card and the nav entry it
  // leads to are recognisably the same destination.
  HelpCircle,
  FileQuestion,
  // Announcements — the broadcast metaphor, matching the Bulk SMS page.
  Megaphone,
  // Same icon the sidebar uses for Message Feedback, and the same pair the
  // feedback table rates rows with — card, nav entry and destination all read
  // as one thing.
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { handleApiError } from '../utils/errorHandler';
import SkeletonCard from '../components/SkeletonCard';
import CoinStatistics from '../components/CoinStatistics';
// Shared with the SMS Costs page so the card and the table format money
// identically — sub-cent totals stay visible instead of rounding to $0.00.
import { formatCost } from '../components/admin/sms-costs/costHelpers';
import '../styles/Dashboard.css';

// Lazy load heavy components
// Only loaded when an admin actually opens the drill-down.

const ConciergeBookingsPanel = lazy(() =>
  import('../components/dashboard/ConciergeBookingsPanel')
);
const SupportTicketsPanel = lazy(() =>
  import('../components/dashboard/SupportTicketsPanel')
);
const BookingExtensionsPanel = lazy(() =>
  import('../components/dashboard/BookingExtensionsPanel')
);
const SmsCostsPanel = lazy(() =>
  import('../components/dashboard/SmsCostsPanel')
);
const FaqQueriesPanel = lazy(() =>
  import('../components/dashboard/FaqQueriesPanel')
);

// Ranges the four drill-down cards can show — concierge bookings, tickets,
// meeting extensions and announcements. ONE map, not four: they were separate
// and identical, which is only a way for them to drift apart.
//
// Each `key` is a field the dashboard payload already carries, so switching a
// window costs no request.
//
// The map is keyed by the API's OWN range vocabulary (lifetime|24h|7d|30d), so
// a card hands its selected key straight to the panel and the panel hands it
// straight to the API. A second vocabulary here would be a translation layer,
// and translation layers drift.
//
// THREE pills, not four, and 24h is the one missing on purpose: measured across
// the dev database, all four of these cards read ZERO over 24 hours. They count
// low-frequency events — a booking made, an issue reported, a YES-to-extend
// reply, an announcement released — unlike the Messages card, whose traffic can
// carry a 24h pill. A pill that always reads 0 makes a card look broken rather
// than quiet, which is the same reason the feedback card already opens on 7d.
// 24h is still one click away in each panel's own filter.
export const CARD_RANGES = {
  '7d': { key: 'last_7d', label: 'Last 7 days' },
  '30d': { key: 'last_30d', label: 'Last 30 days' },
  // `short` exists because "lifetime" will not fit on a pill sized for "30d".
  lifetime: { key: 'last_all', label: 'All time', short: 'all' },
};

// Order on the toggle, shortest window first, lifetime last — the same shape
// the messages card's toggle already uses.
export const CARD_RANGE_ORDER = ['7d', '30d', 'lifetime'];

// Every one of these cards opens on all time: the panel behind it defaults to
// the same window, and the Bulk SMS history the announcements card opens has no
// date filter at all, so anything narrower here contradicts the list one click
// away.
export const DEFAULT_CARD_RANGE = 'lifetime';

// Ranges the feedback card can show. Two keys per window, because the card is
// two numbers — thumbs up and thumbs down — not one. Both are already in the
// dashboard payload, so switching range costs no request.
export const FEEDBACK_RANGES = {
  '24h': { up: 'up_24h', down: 'down_24h', label: 'Last 24 hours' },
  '7d': { up: 'up_7d', down: 'down_7d', label: 'Last 7 days' },
  '30d': { up: 'up_30d', down: 'down_30d', label: 'Last 30 days' },
};

// Order on the toggle, shortest window first.
export const FEEDBACK_RANGE_ORDER = ['24h', '7d', '30d'];

// The one card that does NOT open on 24h. Feedback is low-volume: on 24h this
// card would read "0 0" most mornings and look broken rather than quiet.
export const DEFAULT_FEEDBACK_RANGE = '7d';

// Ranges the messages card can show. Two keys per window — web and SMS — and
// all six are already in the dashboard payload, so switching costs no request.
//
// `all` is the only lifetime window on the dashboard: the other cards ask "what
// happened lately", this one is also asked "how much has ever gone through".
export const MESSAGE_VOLUME_RANGES = {
  '24h': { web: 'web_24h', sms: 'sms_24h', label: 'Last 24 hours' },
  '30d': { web: 'web_30d', sms: 'sms_30d', label: 'Last 30 days' },
  all: { web: 'web_all', sms: 'sms_all', label: 'Lifetime' },
};

// Order on the toggle, shortest window first, lifetime last.
export const MESSAGE_VOLUME_RANGE_ORDER = ['24h', '30d', 'all'];

// Opens on 30d, not the siblings' 24h. A day's traffic says whether the system
// is alive; a month's says how much it is actually used, which is the question
// this card exists to answer. 24h is one click away and the Active Sessions
// card already reports it.
export const DEFAULT_MESSAGE_VOLUME_RANGE = '30d';

/**
 * The range pills that sit in a card's meta slot.
 *
 * A component rather than four copies of the same nine lines — and the four
 * cards that use it must stay in step, because they sit side by side.
 *
 * `stopPropagation` is load-bearing: the pills live INSIDE the card's own click
 * target, so without it picking a window would also open the panel or navigate
 * away. That is also why every card carrying this is a div with role, not a
 * <button>: a button inside a button is invalid HTML that React warns about and
 * screen readers mis-announce.
 */
const CardRangeToggle = ({ label, value, onChange }) => (
  <div className="range-toggle" role="group" aria-label={label}>
    {CARD_RANGE_ORDER.map((key) => (
      <button
        key={key}
        type="button"
        className={`range-toggle-btn${value === key ? ' is-active' : ''}`}
        aria-pressed={value === key}
        title={CARD_RANGES[key].label}
        onClick={(event) => {
          event.stopPropagation();
          onChange(key);
        }}
      >
        {CARD_RANGES[key].short || key}
      </button>
    ))}
  </div>
);

/** Enter and Space activate a div that behaves as a button or link. */
const activateOnKey = (action) => (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
};

const Dashboard = () => {
  const { user } = useAuth();
  const { isAdmin: hasAdminAccess, loading: permissionsLoading } = usePermissions();
  const { withCache, clearAllCache } = useCache();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false); // Don't show full page loader by default
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [bookingTrends, setBookingTrends] = useState(null);
  const [resourceData, setResourceData] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [twilioBalance, setTwilioBalance] = useState(null);
  const [twilioRefreshing, setTwilioRefreshing] = useState(false);
  // 'loading' until the first balance request settles, then 'ready' | 'error'.
  // The card is always rendered; this only picks what goes inside it.
  const [twilioStatus, setTwilioStatus] = useState('loading');

  // AbortController for API cleanup (prevents memory leaks)
  const abortControllerRef = useRef(null);

  // Monotonic run id for loadDashboardData — deferred setters bail out when a
  // newer run has started (branch switch or unmount), so a superseded run can
  // never overwrite fresh data.
  const loadRunRef = useRef(0);

  if (process.env.NODE_ENV !== 'production') {
    logger.debug('DASHBOARD', 'Current user data:', user);
    logger.debug('DASHBOARD', 'User ID:', user?.id);
    logger.debug('DASHBOARD', 'User role:', user?.role);
  }

  // Use RBAC permission context, plus the roles already on the profile (the
  // login/profile response carries RBAC roles — same source PermissionContext
  // uses to decide whether to call the permission API). The profile check makes
  // isAdmin correct on the very first render after login, before the
  // PermissionContext round-trip resolves; without it the data effect fetched
  // the regular-user dashboard and never re-fetched, leaving the admin
  // dashboard empty until a page reload.
  const profileRoles = user?.roles || (user?.role ? [user.role] : []);
  const profileElevated = profileRoles.some(r => r && r !== 'user' && r !== 'guest');
  const isAdmin = hasAdminAccess || profileElevated;

  // Redirect normal users to chat page (wait for permissions to load first)
  useEffect(() => {
    if (user && !permissionsLoading && !isAdmin && user.status === 'approved') {
      logger.info('DASHBOARD', 'Redirecting normal user to chat page', { userId: user.id, role: user.role });
      navigate('/chat', { replace: true });
    }
  }, [user, permissionsLoading, isAdmin, navigate]);

  // Function to calculate user preferences from booking records
  // Memoized to prevent recalculation on every render
  const calculateUserPreferences = useCallback((bookings) => {
    if (!bookings || bookings.length === 0) {
      return {
        preferred_location: null,
        preferred_resource_type: null,
        preferred_resource_name: null,
        most_active_day: null,
        most_active_time: null,
        total_bookings: 0,
        confirmed_bookings: 0,
        cancelled_bookings: 0,
        location_counts: {},
        resource_type_counts: {},
        resource_name_counts: {}
      };
    }

    // Count locations, resources, and status
    const locationCounts = {};
    const resourceTypeCounts = {};
    const resourceNameCounts = {};
    const dayCounts = {};
    const hourCounts = {};
    let confirmedCount = 0;
    let cancelledCount = 0;

    bookings.forEach(booking => {
      // Count locations (try multiple possible field names)
      const location = booking.location_name || booking.location || booking.venue || booking.office_name;
      if (location) {
        locationCounts[location] = (locationCounts[location] || 0) + 1;
      }
      
      // Count resource types
      const resourceType = booking.resource_type || booking.type || booking.space_type;
      if (resourceType) {
        resourceTypeCounts[resourceType] = (resourceTypeCounts[resourceType] || 0) + 1;
      }
      
      // Count resource names (try multiple possible field names)
      const resourceName = booking.resource_name || booking.space_name || booking.room_name || booking.title;
      if (resourceName) {
        resourceNameCounts[resourceName] = (resourceNameCounts[resourceName] || 0) + 1;
      }
      
      // Count booking status
      const status = booking.status || booking.booking_status;
      if (status) {
        if (status.toLowerCase() === 'confirmed') {
          confirmedCount++;
        } else if (status.toLowerCase() === 'cancelled') {
          cancelledCount++;
        }
      }
      
      // Count days of week (try multiple date field names)
      const startDate = booking.start_datetime || booking.start_time || booking.start_date || booking.from;
      if (startDate) {
        const date = new Date(startDate);
        const day = date.toLocaleDateString('en-US', { weekday: 'long' });
        dayCounts[day] = (dayCounts[day] || 0) + 1;
        
        // Count time slots
        const hour = date.getHours();
        const timeSlot = hour < 12 ? 'Morning' : hour < 17 ? 'Afternoon' : 'Evening';
        hourCounts[timeSlot] = (hourCounts[timeSlot] || 0) + 1;
      }
    });

    // Find most frequent values
    const getTopChoice = (counts) => {
      const entries = Object.entries(counts);
      if (entries.length === 0) return null;
      return entries.reduce((a, b) => counts[a[0]] > counts[b[0]] ? a : b)[0];
    };

    const preferences = {
      preferred_location: getTopChoice(locationCounts),
      preferred_resource_type: getTopChoice(resourceTypeCounts),
      preferred_resource_name: getTopChoice(resourceNameCounts),
      most_active_day: getTopChoice(dayCounts),
      most_active_time: getTopChoice(hourCounts),
      total_bookings: bookings.length,
      confirmed_bookings: confirmedCount,
      cancelled_bookings: cancelledCount,
      location_counts: locationCounts,
      resource_type_counts: resourceTypeCounts,
      resource_name_counts: resourceNameCounts
    };

    return preferences;
  }, []); // No dependencies - pure function

  // Wait for the permission round-trip before choosing a fetch branch, unless
  // the profile already marks the user elevated (then fetch admin data right
  // away). Re-runs when isAdmin flips so an elevation known only to
  // PermissionContext still triggers the admin fetch.
  const permissionsReady = isAdmin || !permissionsLoading;

  useEffect(() => {
    if (user?.id && permissionsReady) {
      loadDashboardData(false);
    }

    // Cleanup: invalidate the in-flight run and abort on re-run/unmount
    return () => {
      loadRunRef.current += 1;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run only when user?.id, permissionsReady, isAdmin change; loadDashboardData is defined in render and reads the latest state
  }, [user?.id, permissionsReady, isAdmin]);

  // Manual refresh for the Twilio balance card.
  // ?refresh=true tells the backend to bypass its 30s cache and pull live from Twilio,
  // so fetched_at will be ~0s old after a successful click. Auto-poll below still uses
  // the cached path to stay polite.
  // Stable identity so the polling effect doesn't tear down on each render.
  const refreshTwilioBalance = useCallback(async () => {
    setTwilioRefreshing(true);
    try {
      const data = await adminAPI.monitoring.getTwilioBalance({
        skipErrorToast: true,
        params: { refresh: true },
      });
      setTwilioBalance(data);
      setTwilioStatus(hasUsableBalance(data) ? 'ready' : 'error');
    } catch {
      setTwilioBalance(null);
      setTwilioStatus('error');
    } finally {
      setTwilioRefreshing(false);
    }
  }, []);

  // Poll Twilio balance every 5 min, but only while the dashboard is mounted
  // AND the tab is visible. Backend caches 30s so this is already cheap; this
  // just avoids pointless network when the admin isn't looking.
  useEffect(() => {
    if (!isAdmin || !user?.id) return;

    const POLL_MS = 5 * 60 * 1000;
    let intervalId = null;

    const fetchOnce = () => {
      adminAPI.monitoring
        .getTwilioBalance({ skipErrorToast: true })
        .then(data => {
          setTwilioBalance(data);
          setTwilioStatus(hasUsableBalance(data) ? 'ready' : 'error');
        })
        .catch(() => {
          setTwilioBalance(null);
          setTwilioStatus('error');
        });
    };

    const startPolling = () => {
      if (intervalId) return;
      fetchOnce(); // immediate refresh on (re)gaining visibility
      intervalId = setInterval(fetchOnce, POLL_MS);
    };

    const stopPolling = () => {
      if (!intervalId) return;
      clearInterval(intervalId);
      intervalId = null;
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') startPolling();
      else stopPolling();
    };

    if (document.visibilityState === 'visible') startPolling();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAdmin, user?.id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadDashboardData is rebuilt every render on purpose (reads current page/filters); callbacks using it always call the latest one
  const loadDashboardData = async (forceRefresh = false) => {
    abortControllerRef.current = new AbortController();
    const runId = ++loadRunRef.current;
    const isStale = () => runId !== loadRunRef.current;

    try {
      const userId = user?.id;
      if (!userId) return;

      // Clear cache on force refresh
      if (forceRefresh) {
        clearAllCache();
      }

      if (isAdmin) {
        // Call admin APIs — backend enforces permissions.
        // Use skipErrorToast so 403s from endpoints the user can't access
        // are silently caught (data stays null → UI shows empty state).
        const dashboardPromise = withCache(
          'admin_dashboard',
          () => analyticsAPI.getDashboard({ skipErrorToast: true })
        );

        const usersPromise = withCache(
          'admin_users',
          () => analyticsAPI.getAllUsersAnalytics({ limit: 100, skipErrorToast: true })
        );

        // Show cached data immediately if available
        const [dashboard, users] = await Promise.all([
          dashboardPromise.catch(() => null),
          usersPromise.catch(() => null)
        ]);

        if (isStale()) return;
        setDashboardData(dashboard);
        setUserAnalytics(users);
        setLoading(false);

        // Load secondary data lazily (don't block UI)
        setTimeout(() => {
          if (isStale()) return;

          // Trends
          withCache(
            'admin_trends',
            () => analyticsAPI.getBookingTrends(30, { skipErrorToast: true })
          ).then(data => { if (!isStale()) setBookingTrends(data); }).catch((error) => {
            if (error.name === 'AbortError') return;
          });

          // Resources
          withCache(
            'admin_resources',
            () => analyticsAPI.getResourceAnalytics({ skipErrorToast: true })
          ).then(data => { if (!isStale()) setResourceData(data); }).catch((error) => {
            if (error.name === 'AbortError') return;
          });

          // Health
          withCache(
            'admin_health',
            () => analyticsAPI.getSystemHealth({ skipErrorToast: true })
          ).then(data => { if (!isStale()) setSystemHealth(data); }).catch((error) => {
            if (error.name === 'AbortError') return;
          });

          // Twilio balance is fetched by its own polling effect (mount + visibilitychange).
        }, 100);

      } else {
        // User dashboard with efficient caching (via CacheContext)
        const profilePromise = withCache(
          'user_profile',
          () => analyticsAPI.getUserProfile()
        );

        // Get cached profile immediately
        const profile = await profilePromise.catch(() => null);

        if (isStale()) return;
        if (profile) {
          setUserAnalytics({
            ...profile,
            booking_analytics: profile?.booking_analytics || {},
            communication_analytics: profile?.communication_analytics || {},
            activity_metrics: profile?.activity_metrics || {}
          });
          setLoading(false);
        }

        // Load other data lazily
        setTimeout(() => {
          if (isStale()) return;

          // Summary
          withCache(
            'user_summary',
            () => analyticsAPI.getUserBookingSummary()
          ).then(summary => { if (!isStale()) setDashboardData(summary); }).catch((error) => {
            if (error.name === 'AbortError') return; // Ignore abort
          });

          // Bookings for preferences (heavier call)
          withCache(
            'user_bookings',
            () => userAPI.getBookings({ limit: 100 })
          ).then(bookings => {
            if (isStale()) return;
            const calculatedPreferences = calculateUserPreferences(bookings?.bookings || bookings || []);
            
            // Update user analytics with preferences
            setUserAnalytics(prev => ({
              ...prev,
              booking_analytics: {
                ...prev?.booking_analytics,
                calculated_preferences: calculatedPreferences,
                total_bookings: calculatedPreferences.total_bookings,
                favorite_locations: calculatedPreferences.location_counts ? 
                  Object.entries(calculatedPreferences.location_counts)
                    .map(([location_name, count]) => ({ location_name, count }))
                    .sort((a, b) => b.count - a.count) : [],
                favorite_resources: calculatedPreferences.resource_name_counts ?
                  Object.entries(calculatedPreferences.resource_name_counts)
                    .map(([resource_name, count]) => ({
                      resource_name,
                      resource_type: calculatedPreferences.resource_type_counts[resource_name] || 'unknown',
                      count
                    }))
                    .sort((a, b) => b.count - a.count) : [],
                booking_trends: {
                  most_active_day: calculatedPreferences.most_active_day,
                  most_active_time: calculatedPreferences.most_active_time
                }
              }
            }));
          }).catch((error) => {
            if (error.name === 'AbortError') return; // Ignore abort
          });
        }, 100);
      }
    } catch (error) {
      // Don't show error if request was aborted or the run was superseded
      if (error.name === 'AbortError' || isStale()) {
        return;
      }

// Only show error messages for actual errors, not for empty data
      if (error.response?.status === 403 || error.response?.status === 401) {
        handleApiError(error, 'DASHBOARD', 'Failed to load dashboard data');
      }
      // Don't show error messages for 404 or other errors - just show empty state

      // Set empty data on error

logger.debug('DASHBOARD', 'Dashboard loading failed, setting empty state');
      if (isAdmin) {
        setDashboardData(null);
        setBookingTrends(null);
        setResourceData(null);
        setSystemHealth(null);
        setUserAnalytics(null);
      } else {
        setUserAnalytics(null);
        setDashboardData(null);
      }
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  };

  // ✅ P2-21: Memoize handler to prevent recreation on every render
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    // Don't clear cache on refresh - just force fresh data
    await loadDashboardData(true);
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  }, [loadDashboardData]);

  // ✅ PROGRESSIVE LOADING: Show layout immediately, not full-page blocking
  return (
    <div className="dashboard-container">

      {/* Header - Always visible immediately */}
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">
            {isAdmin ? 'Admin Dashboard' : 'My Dashboard'}
          </h1>
          <p className="dashboard-subtitle">
            {isAdmin
              ? 'System overview and analytics'
              : `Welcome back, ${user?.first_name || 'User'}!`
            }
          </p>
        </div>
        <button
          onClick={handleRefresh}
          className="btn btn-outline btn-refresh"
          disabled={refreshing}
          aria-label="Refresh dashboard data"
          aria-busy={refreshing}
        >
          <RefreshCw size={18} className={refreshing ? 'spinning' : ''} aria-hidden="true" />
          Refresh
        </button>
      </div>

      {/* Content - Progressive loading with skeletons */}
      {loading ? (
        <div className="dashboard-grid overview-grid">
          <SkeletonCard count={4} />
        </div>
      ) : (
        isAdmin ? (
          <AdminDashboard
            dashboardData={dashboardData}
            bookingTrends={bookingTrends}
            resourceData={resourceData}
            systemHealth={systemHealth}
            userAnalytics={userAnalytics}
            twilioBalance={twilioBalance}
            twilioStatus={twilioStatus}
            twilioRefreshing={twilioRefreshing}
            onRefreshTwilio={refreshTwilioBalance}
          />
        ) : (
          <UserDashboard
            userAnalytics={userAnalytics}
            dashboardData={dashboardData}
          />
        )
      )}
    </div>
  );
};

// A balance payload is only usable if it actually carries a numeric amount —
// a resolved-but-empty response counts as an error for the card.
const hasUsableBalance = (data) => typeof data?.balance_float === 'number';

// Format an ISO timestamp as a short "Xs ago" / "Xm ago" label.
const formatRefreshedAgo = (iso) => {
  if (!iso) return '';
  // Backend returns UTC without a trailing Z; normalize so Date parses as UTC.
  const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const ms = Date.now() - new Date(normalized).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 'just now';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
};

// Admin Dashboard Component
const AdminDashboard = ({ dashboardData, bookingTrends, resourceData, systemHealth, userAnalytics, twilioBalance, twilioStatus, twilioRefreshing, onRefreshTwilio }) => {
  const navigate = useNavigate();
  // Two cards on this page are not covered by dashboard:view. System Health
  // exposes infrastructure internals (database size, failed logins) and the SMS
  // card is member spend, so each has its own permission.
  const can = useCapabilities({
    health: 'dashboard:system_health',
    smsCosts: 'sms_costs:read',
  });
  // Which card's list is showing. One at a time: the panel belongs to the
  // selection, and two open tables underneath the cards would reintroduce the
  // scrolling mess the inline layout exists to avoid.
  const [openPanel, setOpenPanel] = useState(null);
  // One window per card, not one shared between them: changing the window on
  // the tickets card must not move the bookings number beside it. Same as the
  // announcements, feedback and messages cards, which each own their own.
  const [conciergeRange, setConciergeRange] = useState(DEFAULT_CARD_RANGE);
  const [ticketsRange, setTicketsRange] = useState(DEFAULT_CARD_RANGE);
  const [extensionsRange, setExtensionsRange] = useState(DEFAULT_CARD_RANGE);
  const [smsCostRange, setSmsCostRange] = useState(DEFAULT_CARD_RANGE);
  const togglePanel = (panel) =>
    setOpenPanel((current) => (current === panel ? null : panel));
  const conciergePanelOpen = openPanel === 'concierge';
  const ticketsPanelOpen = openPanel === 'tickets';
  const extensionsPanelOpen = openPanel === 'extensions';
  const faqQueriesPanelOpen = openPanel === 'faq-queries';
  const smsCostPanelOpen = openPanel === 'sms-cost';
  // Both absent on an older backend, or before the migration runs — the cards
  // must render 0 rather than crash.
  const conciergeBookings = dashboardData?.concierge_bookings;
  const supportTickets = dashboardData?.support_tickets;
  const bookingExtensions = dashboardData?.booking_extensions;
  const googleCalendar = dashboardData?.google_calendar;
  // Money, not a row count — the backend sends exact strings so a sub-cent
  // total is not rounded away by JSON.
  const smsCost = dashboardData?.sms_cost;
  // People, not rows: one member can connect several Google accounts, so the
  // calendar total goes in the sub-line where it matches the table this card
  // opens.
  const connectedCalendarUsers = googleCalendar?.connected_users ?? 0;
  const connectedCalendars = googleCalendar?.total_integrations ?? 0;
  // Active is the headline — what the concierge can actually answer from. The
  // full count goes in the sub-line because that is what the Workspace FAQs
  // page lists with no status filter, and the two must not look contradictory.
  const faqs = dashboardData?.faqs;
  const activeFaqs = faqs?.active ?? 0;
  const totalFaqs = faqs?.total ?? 0;
  const pendingFaqs = faqs?.pending ?? 0;
  // What members ASKED that knowledge base, and how much of it we could answer.
  // All-time, like the panel it opens — a window here would hide the oldest
  // unanswered questions, which are exactly the FAQs still to be written.
  const faqUsage = dashboardData?.faq_usage;
  const faqAsked = faqUsage?.asked ?? 0;
  const faqAnswered = faqUsage?.answered ?? 0;
  const faqUnanswered = faqUsage?.unanswered ?? 0;
  // Announcements — both the ones admins composed on the Bulk SMS page and the
  // Community Announcements released from staff chat. That page lists both, so
  // counting one would put a number here that contradicts the list it opens.
  const [announcementRange, setAnnouncementRange] = useState(DEFAULT_CARD_RANGE);
  const announcements = dashboardData?.announcements;
  const announcementWindow = CARD_RANGES[announcementRange];
  const announcementCount = announcements?.[announcementWindow.key] ?? 0;
  const openAnnouncements = () => navigate('/admin/bulk-sms');
  // Message feedback — human thumbs only. The payload also carries `qa_*`, the
  // QA agent's automated findings; they are deliberately NOT shown, because
  // every one of them is a thumbs-down and folding them in would make this card
  // report scan volume instead of how members feel.
  const [feedbackRange, setFeedbackRange] = useState(DEFAULT_FEEDBACK_RANGE);
  const feedback = dashboardData?.feedback;
  const feedbackWindow = FEEDBACK_RANGES[feedbackRange];
  const feedbackUp = feedback?.[feedbackWindow.up] ?? 0;
  const feedbackDown = feedback?.[feedbackWindow.down] ?? 0;
  const openFeedback = () => navigate('/admin/feedback');
  // Message volume — web vs SMS traffic, including a lifetime window. Counts
  // every message either way: inbound and outbound, member, assistant and
  // admin. That is deliberately the same rule as the "messages in last 24
  // hours" line on the Active Sessions card above, so the two numbers on this
  // screen can never contradict each other.
  const [messageVolumeRange, setMessageVolumeRange] = useState(
    DEFAULT_MESSAGE_VOLUME_RANGE
  );
  const messageVolume = dashboardData?.message_volume;
  const messageVolumeWindow = MESSAGE_VOLUME_RANGES[messageVolumeRange];
  const webMessages = messageVolume?.[messageVolumeWindow.web] ?? 0;
  const smsMessages = messageVolume?.[messageVolumeWindow.sms] ?? 0;
  const webMessagesLabel = webMessages.toLocaleString();
  const smsMessagesLabel = smsMessages.toLocaleString();
  // Lifetime counts are five and six digits — two of those at the row's 2rem
  // would run off the card. So the PAIR shrinks when it gets long, rather than
  // being permanently small: on 24h ("96 / 8") this card still reads at exactly
  // the same size as the "27" and "60" beside it.
  const messageDigits = (webMessagesLabel + smsMessagesLabel).replace(
    /[^0-9]/g,
    ''
  ).length;
  const messageVolumeSize =
    messageDigits > 7 ? ' is-tiny' : messageDigits > 5 ? ' is-compact' : '';
  const openLiveConversations = () => navigate('/admin/live-conversations');
  if (process.env.NODE_ENV !== 'production') {
    logger.debug('DASHBOARD', 'AdminDashboard received props:');
    logger.debug('DASHBOARD', 'dashboardData:', dashboardData);
    logger.debug('DASHBOARD', 'bookingTrends:', bookingTrends);
    logger.debug('DASHBOARD', 'resourceData:', resourceData);
    logger.debug('DASHBOARD', 'systemHealth:', systemHealth);
    logger.debug('DASHBOARD', 'userAnalytics:', userAnalytics);
  }
  
  return (
    <div className="admin-dashboard">
      {/* Overview Cards */}
      <div className="dashboard-grid overview-grid">
        {/* The count is the way into the user list — clicking it opens
            User Management rather than leaving admins to find it in the nav. */}
        <button
          type="button"
          className="stat-card users-card stat-card-clickable"
          onClick={() => navigate('/admin')}
        >
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <h3>Total Users</h3>
            <div className="stat-number">{dashboardData?.users?.total?.toLocaleString() || 'N/A'}</div>
            <div className="stat-meta">
              <span className="stat-trend positive">
                {/* ?? not || — a zero-signup day is a real answer, and "N/A"
                    would make it look like the count failed to load. */}
                +{dashboardData?.users?.new_today ?? 'N/A'} today
              </span>
            </div>
          </div>
        </button>

        {/* Total Bookings card removed: the backend has no local booking totals
            (analytics_service_optimized returns 0), so it always read "0", and
            the Booked via Concierge card now carries the real number.
        <div className="stat-card bookings-card">
          <div className="stat-icon">
            <CalendarCheck size={24} />
          </div>
          <div className="stat-content">
            <h3>Total Bookings</h3>
            <div className="stat-number">{dashboardData?.bookings?.total?.toLocaleString() || 'N/A'}</div>
            <div className="stat-meta">
              <span className="stat-trend positive">
                +{dashboardData?.bookings?.today || 'N/A'} today
              </span>
            </div>
          </div>
        </div>
        */}

        <div className="stat-card activity-card">
          <div className="stat-icon">
            <Activity size={24} />
          </div>
          <div className="stat-content">
            <h3>Active Sessions</h3>
            {/* Rolling last 24 hours, not calendar-day: right after midnight a
                "today" count reads as a dead system. */}
            <div className="stat-number">{dashboardData?.activity?.active_sessions_24h ?? 0}</div>
            <div className="stat-meta">
              <span className="stat-trend">
                {dashboardData?.activity?.messages_24h ?? 0} messages in last 24 hours
              </span>
            </div>
          </div>
        </div>

        {/* Concierge bookings — counts come from the dashboard payload already
            in hand, so switching range is instant. Opening the panel fetches
            its own first page, on whatever window is selected here.

            A div with role="button" rather than a <button> like the plain
            cards: the range toggle is made of buttons, and a button inside a
            button is invalid HTML. Same pattern as the announcements and
            feedback cards. The "view list" hint the meta line used to carry
            lives in aria-label now — the toggle took that slot, and a second
            meta line would make this card taller than the ones beside it. */}
        <div
          className={`stat-card concierge-card stat-card-clickable${
            conciergePanelOpen ? ' stat-card-selected' : ''
          }`}
          role="button"
          tabIndex={0}
          aria-label={`Booked via Concierge, ${
            conciergePanelOpen ? 'hide list' : 'view list'
          }`}
          aria-pressed={conciergePanelOpen}
          aria-expanded={conciergePanelOpen}
          onClick={() => togglePanel('concierge')}
          onKeyDown={activateOnKey(() => togglePanel('concierge'))}
        >
          <div className="stat-icon">
            <CalendarCheck size={24} />
          </div>
          <div className="stat-content">
            <h3>Booked via Concierge</h3>
            <div className="stat-number">
              {conciergeBookings?.[CARD_RANGES[conciergeRange].key] ?? 0}
            </div>
            <div className="stat-meta">
              <CardRangeToggle
                label="Concierge bookings range"
                value={conciergeRange}
                onChange={setConciergeRange}
              />
            </div>
          </div>
        </div>

        {/* Support tickets — counts only in the payload; the panel fetches its
            rows when an admin opens it. */}
        <div
          className={`stat-card tickets-card stat-card-clickable${
            ticketsPanelOpen ? ' stat-card-selected' : ''
          }`}
          role="button"
          tabIndex={0}
          aria-label={`Tickets Reported, ${
            ticketsPanelOpen ? 'hide list' : 'view list'
          }`}
          aria-pressed={ticketsPanelOpen}
          aria-expanded={ticketsPanelOpen}
          onClick={() => togglePanel('tickets')}
          onKeyDown={activateOnKey(() => togglePanel('tickets'))}
        >
          <div className="stat-icon">
            <LifeBuoy size={24} />
          </div>
          <div className="stat-content">
            <h3>Tickets Reported</h3>
            <div className="stat-number">
              {supportTickets?.[CARD_RANGES[ticketsRange].key] ?? 0}
            </div>
            <div className="stat-meta">
              {/* Awaiting-response count hidden for now — uncomment to restore.
                  It counts a rolling 30 days, which read oddly beside a card
                  showing a different window.
              {supportTickets?.awaiting_response > 0 ? (
                <span className="stat-trend warning">
                  {supportTickets.awaiting_response} awaiting response
                </span>
              ) : null}
              */}
              <CardRangeToggle
                label="Tickets reported range"
                value={ticketsRange}
                onChange={setTicketsRange}
              />
            </div>
          </div>
        </div>

        {/* Meeting extensions — counts only in the payload; the panel fetches
            its rows when an admin opens it. Reminder-driven extensions (the
            YES-to-extend SMS) only: a member who pushes their end time in
            normal chat is a plain booking update with no extension marker. */}
        <div
          className={`stat-card extensions-card stat-card-clickable${
            extensionsPanelOpen ? ' stat-card-selected' : ''
          }`}
          role="button"
          tabIndex={0}
          aria-label={`Meeting Extensions, ${
            extensionsPanelOpen ? 'hide list' : 'view list'
          }`}
          aria-pressed={extensionsPanelOpen}
          aria-expanded={extensionsPanelOpen}
          onClick={() => togglePanel('extensions')}
          onKeyDown={activateOnKey(() => togglePanel('extensions'))}
        >
          <div className="stat-icon">
            <TimerReset size={24} />
          </div>
          <div className="stat-content">
            <h3>Meeting Extensions</h3>
            <div className="stat-number">
              {bookingExtensions?.[CARD_RANGES[extensionsRange].key] ?? 0}
            </div>
            <div className="stat-meta">
              {/* Count + range only. The minutes-added total was a second
                  number competing with the one the card is about. */}
              <CardRangeToggle
                label="Meeting extensions range"
                value={extensionsRange}
                onChange={setExtensionsRange}
              />
            </div>
          </div>
        </div>

        {/* Google Calendar — the count is the way into the All User Calendars
            table, the same view the "View All Users" button on that page opens.
            No panel here: that table already exists, so linking beats building
            a second one. */}
        <button
          type="button"
          className="stat-card calendar-connect-card stat-card-clickable"
          onClick={() => navigate('/google-calendar?view=all-users')}
        >
          <div className="stat-icon">
            <CalendarClock size={24} />
          </div>
          <div className="stat-content">
            <h3>Google Calendar Connected</h3>
            <div className="stat-number">
              {connectedCalendarUsers.toLocaleString()}
            </div>
            <div className="stat-meta">
              <span className="stat-trend">
                {connectedCalendars.toLocaleString()}{' '}
                {connectedCalendars === 1 ? 'calendar' : 'calendars'} · view users
              </span>
            </div>
          </div>
        </button>

        {/* Total FAQs — the count is the way into the Workspace FAQs page.
            No panel here: that page already lists, filters and edits FAQs, so
            linking beats building a second table. Scoped like the page it
            opens: a manager sees their locations' FAQs plus the global ones. */}
        <button
          type="button"
          className="stat-card faqs-card stat-card-clickable"
          onClick={() => navigate('/workspace-faqs')}
        >
          <div className="stat-icon">
            <HelpCircle size={24} />
          </div>
          <div className="stat-content">
            <h3>Total FAQs</h3>
            <div className="stat-number">{activeFaqs.toLocaleString()}</div>
            <div className="stat-meta">
              <span className="stat-trend">
                {totalFaqs.toLocaleString()} total · view FAQs
              </span>
              {/* Only when there is something to act on — a permanent
                  "0 pending" is noise on a card admins scan, not information. */}
              {pendingFaqs > 0 ? (
                <span className="stat-trend warning">
                  {pendingFaqs.toLocaleString()} pending review
                </span>
              ) : null}
            </div>
          </div>
        </button>

        {/* FAQ Questions — the companion to the card above. That one says how
            big the knowledge base is; this one says whether it was enough.
            Three numbers on purpose: asked = answered + couldn't answer, always.
            The third is the one worth acting on, so it opens the panel already
            filtered to it. Counts only in the payload; the panel fetches its
            rows when an admin opens it. */}
        <button
          type="button"
          className={`stat-card faq-questions-card stat-card-clickable${
            faqQueriesPanelOpen ? ' stat-card-selected' : ''
          }`}
          onClick={() => togglePanel('faq-queries')}
          aria-pressed={faqQueriesPanelOpen}
          aria-expanded={faqQueriesPanelOpen}
        >
          <div className="stat-icon">
            <FileQuestion size={24} />
          </div>
          <div className="stat-content">
            <h3>FAQ Questions</h3>
            <div className="stat-number">{faqAsked.toLocaleString()}</div>
            {/* One short line. "261 answered · 125 couldn't answer · view
                list" is 45 characters and wrapped onto three lines in this
                column, making the card taller than every other card in the
                row. "unanswered" says the same thing in five fewer, and the
                action hint goes: the card is visibly clickable, takes a
                selected border while open, and the list appears directly
                beneath it. */}
            <div className="stat-meta">
              <span className="stat-trend faq-questions-meta">
                {faqAnswered.toLocaleString()} answered
                {/* Warning-toned only when there is something to act on — a
                    permanent "0 unanswered" is noise on a card admins scan,
                    and the colour stops meaning anything. */}
                {faqUnanswered > 0 ? (
                  <>
                    {' · '}
                    <span className="stat-trend-alert">
                      {faqUnanswered.toLocaleString()} unanswered
                    </span>
                  </>
                ) : null}
              </span>
            </div>
          </div>
        </button>

        {/* Community Announcements — the count is the way into the Bulk SMS
            page, which is where announcements are composed AND where the
            history of every one of them lives, whichever way it was created.

            A div with role="link" rather than a <button> like the cards above:
            the range toggle is made of buttons, and a button inside a button
            is invalid HTML that React warns about and screen readers
            mis-announce. Same pattern as the locations card further down. */}
        <div
          className="stat-card announcements-card stat-card-clickable"
          role="link"
          tabIndex={0}
          aria-label="Community Announcements"
          onClick={openAnnouncements}
          onKeyDown={activateOnKey(openAnnouncements)}
        >
          <div className="stat-icon">
            <Megaphone size={24} />
          </div>
          <div className="stat-content">
            {/* Heading, number, then meta — the same three rows, in the same
                order, as every sibling card. The range toggle takes the meta
                slot, and every card in this row now carries the same one. */}
            <h3>Community Announcements</h3>
            <div className="stat-number">
              {announcementCount.toLocaleString()}
            </div>
            <div className="stat-meta">
              <CardRangeToggle
                label="Announcement range"
                value={announcementRange}
                onChange={setAnnouncementRange}
              />
            </div>
          </div>
        </div>

        {/* Message Feedback — how members rated our replies, as the way into the
            page where every rating is listed and worked through.

            A div with role="link" rather than a <button>, same as the
            announcements card above: the range toggle is made of buttons, and a
            button inside a button is invalid HTML that React warns about and
            screen readers mis-announce. */}
        <div
          className="stat-card feedback-card stat-card-clickable"
          role="link"
          tabIndex={0}
          aria-label="Message Feedback"
          onClick={openFeedback}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openFeedback();
            }
          }}
        >
          <div className="stat-icon">
            <ThumbsUp size={24} />
          </div>
          <div className="stat-content">
            {/* Heading, number, then meta — the same three rows as every sibling
                card. The difference is that the number row holds TWO numbers:
                up and down are only meaningful next to each other, and splitting
                them across two cards would double the row for half the story. */}
            <h3>Message Feedback</h3>
            <div className="stat-number feedback-thumbs">
              <span
                className="feedback-thumb feedback-thumb-up"
                aria-label={`${feedbackUp} thumbs up`}
              >
                {feedbackUp.toLocaleString()}
                <ThumbsUp size={20} aria-hidden="true" />
              </span>
              <span
                className="feedback-thumb feedback-thumb-down"
                aria-label={`${feedbackDown} thumbs down`}
              >
                {feedbackDown.toLocaleString()}
                <ThumbsDown size={20} aria-hidden="true" />
              </span>
            </div>
            <div className="stat-meta">
              <div
                className="range-toggle"
                role="group"
                aria-label="Feedback range"
              >
                {FEEDBACK_RANGE_ORDER.map((range) => (
                  <button
                    key={range}
                    type="button"
                    className={`range-toggle-btn${
                      feedbackRange === range ? ' is-active' : ''
                    }`}
                    aria-pressed={feedbackRange === range}
                    title={FEEDBACK_RANGES[range].label}
                    onClick={(e) => {
                      // The card itself is the click target — without this,
                      // picking a window would navigate away instead.
                      e.stopPropagation();
                      setFeedbackRange(range);
                    }}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Messages — how much traffic came through, split web vs SMS, as the
            way into Live Conversations where those messages are actually read.

            A div with role="link" rather than a <button>, same reason as the
            two cards above: the range toggle is made of buttons. */}
        <div
          className="stat-card messages-volume-card stat-card-clickable"
          role="link"
          tabIndex={0}
          aria-label="Messages"
          onClick={openLiveConversations}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openLiveConversations();
            }
          }}
        >
          <div className="stat-icon">
            <MessageSquare size={24} />
          </div>
          <div className="stat-content">
            {/* Heading, number, meta — the same three rows as every sibling.
                Web first: it is the overwhelming majority of traffic, so the
                bigger number reads first and SMS is the comparison. */}
            <h3>Messages</h3>
            {/* The channel is spelled out, not drawn. A speech bubble and a
                phone are the same idea to most people — "web" and "sms" are
                read correctly the first time, and they take less room than the
                glyphs did, which is what let the lifetime numbers fit. */}
            <div className={`stat-number channel-counts${messageVolumeSize}`}>
              <span
                className="channel-count channel-count-web"
                aria-label={`${webMessages} web messages`}
              >
                {webMessagesLabel}
                <span className="channel-tag" aria-hidden="true">web</span>
              </span>
              <span
                className="channel-count channel-count-sms"
                aria-label={`${smsMessages} SMS messages`}
              >
                {smsMessagesLabel}
                <span className="channel-tag" aria-hidden="true">sms</span>
              </span>
            </div>
            <div className="stat-meta">
              <div
                className="range-toggle"
                role="group"
                aria-label="Message volume range"
              >
                {MESSAGE_VOLUME_RANGE_ORDER.map((range) => (
                  <button
                    key={range}
                    type="button"
                    className={`range-toggle-btn${
                      messageVolumeRange === range ? ' is-active' : ''
                    }`}
                    aria-pressed={messageVolumeRange === range}
                    title={MESSAGE_VOLUME_RANGES[range].label}
                    onClick={(e) => {
                      // The card itself is the click target — without this,
                      // picking a window would navigate away instead.
                      e.stopPropagation();
                      setMessageVolumeRange(range);
                    }}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* What our text messaging costs. Money, not a count — rendered from
            the exact string the backend sends, never re-computed here. */}
        <div
          hidden={!can.smsCosts}
          className={`stat-card sms-cost-card stat-card-clickable${
            smsCostPanelOpen ? ' stat-card-selected' : ''
          }`}
          role="button"
          tabIndex={0}
          aria-label={`SMS spend, ${smsCostPanelOpen ? 'hide list' : 'view list'}`}
          aria-pressed={smsCostPanelOpen}
          aria-expanded={smsCostPanelOpen}
          onClick={() => togglePanel('sms-cost')}
          onKeyDown={activateOnKey(() => togglePanel('sms-cost'))}
        >
          <div className="stat-icon">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <h3>SMS Spend</h3>
            <div className="stat-number">
              {formatCost(smsCost?.[CARD_RANGES[smsCostRange].key])}
            </div>
            <div className="stat-meta">
              <CardRangeToggle
                label="SMS spend range"
                value={smsCostRange}
                onChange={setSmsCostRange}
              />
            </div>
          </div>
        </div>

        {/* Always rendered so the card never pops in or disappears — only its
            body switches between loading / balance / unavailable. */}
        <div className="stat-card twilio-card">
          <div className="stat-icon">
            <Phone size={24} />
          </div>
          <div className="stat-content">
            <div className="twilio-card-header">
              <h3>Twilio Balance</h3>
              <button
                type="button"
                className="twilio-refresh-btn"
                onClick={onRefreshTwilio}
                disabled={twilioRefreshing}
                aria-label="Refresh Twilio balance"
                aria-busy={twilioRefreshing}
                title="Refresh now"
              >
                <RefreshCw size={14} className={twilioRefreshing ? 'spinning' : ''} aria-hidden="true" />
              </button>
            </div>

            {twilioStatus === 'ready' ? (
              <>
                <div
                  className={`stat-number balance-${
                    twilioBalance.balance_float < 5
                      ? 'critical'
                      : twilioBalance.low_balance
                      ? 'low'
                      : 'ok'
                  }`}
                >
                  ${twilioBalance.balance_float.toFixed(2)} {twilioBalance.currency}
                </div>
                <div className="stat-meta">
                  {twilioBalance.low_balance ? (
                    <span className="stat-trend warning">⚠ Low balance</span>
                  ) : (
                    <span className="stat-trend">
                      Refreshed {formatRefreshedAgo(twilioBalance.fetched_at)}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="stat-number balance-unknown" aria-live="polite">
                  {twilioStatus === 'loading' ? '· · ·' : '—'}
                </div>
                <div className="stat-meta">
                  <span className="stat-trend">
                    {twilioStatus === 'loading' ? 'Checking balance...' : 'Balance unavailable'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Drill-down list for the selected card — inline, directly beneath the
          cards, so the numbers stay visible while the admin reads the rows. */}
      {conciergePanelOpen && (
        <Suspense fallback={null}>
          <ConciergeBookingsPanel
            range={conciergeRange}
            onClose={() => setOpenPanel(null)}
          />
        </Suspense>
      )}

      {smsCostPanelOpen && can.smsCosts && (
        <Suspense fallback={null}>
          <SmsCostsPanel
            range={smsCostRange}
            onClose={() => setOpenPanel(null)}
          />
        </Suspense>
      )}

      {ticketsPanelOpen && (
        <Suspense fallback={null}>
          <SupportTicketsPanel
            range={ticketsRange}
            onClose={() => setOpenPanel(null)}
          />
        </Suspense>
      )}

      {extensionsPanelOpen && (
        <Suspense fallback={null}>
          <BookingExtensionsPanel
            range={extensionsRange}
            onClose={() => setOpenPanel(null)}
          />
        </Suspense>
      )}

      {/* No `range` prop: the FAQ card carries no toggle, so this panel just
          takes its own all-time default. */}
      {faqQueriesPanelOpen && (
        <Suspense fallback={null}>
          <FaqQueriesPanel onClose={() => setOpenPanel(null)} />
        </Suspense>
      )}

      {/* Charts and Analytics */}
      <div className="dashboard-grid analytics-grid">
        {/* Booking Trends Chart - Hidden for now (broken/incorrect data, will re-enable after fix)
        <div className="analytics-card trends-card">
          <div className="card-header">
            <h3>Booking Trends (30 Days)</h3>
            <BarChart3 size={20} />
          </div>
          <div className="chart-container">
            <BookingTrendsChart data={bookingTrends} />
          </div>
        </div>
        */}

        {/* Resource Analytics - Hidden for now
        <div className="analytics-card resources-card">
          <div className="card-header">
            <h3>Resource Utilization</h3>
            <PieChart size={20} />
          </div>
          <div className="resource-list">
            {resourceData?.resource_types?.map((resource) => (
              <div key={resource.resource_type} className="resource-item">
                <div className="resource-info">
                  <h4>{resource.resource_type.replace('_', ' ')}</h4>
                  <span className="resource-stats">
                    {resource.total_bookings} bookings • {resource.utilization_rate}% utilized
                  </span>
                </div>
                <div className="resource-utilization">
                  <div
                    className="utilization-bar"
                    style={{ width: `${resource.utilization_rate}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        */}

        {/* System Health - Hidden for now
        <div className="analytics-card health-card" hidden={!can.health}>
          <div className="card-header">
            <h3>System Health</h3>
            <Shield size={20} />
          </div>
          <div className="health-metrics">
            <div className="health-item">
              <div className="health-label">Database Status</div>
              <div className={`health-status ${systemHealth?.status === 'healthy' ? 'healthy' : 'warning'}`}>
                {systemHealth?.status === 'healthy' ? 'Healthy' : 'Warning'}
              </div>
            </div>
            <div className="health-item">
              <div className="health-label">Response Time</div>
              <div className="health-value">
                {systemHealth?.performance?.avg_response_time_ms || 0}ms
              </div>
            </div>
            <div className="health-item">
              <div className="health-label">Login Success Rate</div>
              <div className="health-value">
                {systemHealth?.authentication?.login_success_rate || 0}%
              </div>
            </div>
            <div className="health-item">
              <div className="health-label">Errors Today</div>
              <div className={`health-value ${(systemHealth?.performance?.error_events_today || 0) > 5 ? 'warning' : 'good'}`}>
                {systemHealth?.performance?.error_events_today || 0}
              </div>
            </div>
          </div>
        </div>
        */}

        {/* Top Active Users - Hidden for now (broken/incorrect data, will re-enable after fix)
        <div className="analytics-card top-users-card">
          <div className="card-header">
            <h3>Top Active Users</h3>
            <Users size={20} />
          </div>
          <div className="top-users-list">
            {userAnalytics?.users?.slice(0, 10).map((user, index) => (
              <div key={user.id} className="top-user-item">
                <div className="user-rank">{index + 1}</div>
                <div className="user-info">
                  <div className="user-name">{user.first_name} {user.last_name}</div>
                  <div className="user-stats">{user.total_bookings} bookings</div>
                </div>
                <div className="user-status">
                  <span className={`status-badge ${user.status}`}>{user.status}</span>
                </div>
              </div>
            )) || (
              <div className="no-data">
                <Users size={32} />
                <p>No user data available</p>
              </div>
            )}
          </div>
        </div>
        */}
      </div>

      {/* Gantt Chart Timeline - Commented out (booking_records table removed)
          Bookings are now fetched from OfficeRND API in real-time, not stored locally.
          This chart would always be empty since we don't store booking history.

      <div className="dashboard-grid gantt-section">
        <div className="gantt-wrapper">
          <Suspense fallback={
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading timeline...
            </div>
          }>
            <GanttChartV2 autoRefresh={true} />
          </Suspense>
        </div>
      </div>
      */}

      {/* Quick Links */}
      {/* Location Management quick-link card removed per request
      <div className="dashboard-grid quick-links-grid">
        <div
          className="quick-link-card"
          onClick={() => navigate('/admin/locations')}
          onKeyPress={(e) => e.key === 'Enter' && navigate('/admin/locations')}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          aria-label="Navigate to location management"
        >
          <div className="quick-link-icon">
            <MapPin size={24} aria-hidden="true" />
          </div>
          <div className="quick-link-content">
            <h4>Location Management</h4>
            <p>Manage office locations and sync from OfficeRND</p>
          </div>
          <div className="quick-link-arrow">
            <Settings size={20} aria-hidden="true" />
          </div>
        </div>
      </div>
      */}
    </div>
  );
};

// User Dashboard Component
const UserDashboard = ({ userAnalytics, dashboardData }) => {
  if (process.env.NODE_ENV !== 'production') {
    logger.debug('DASHBOARD', 'UserDashboard userAnalytics:', userAnalytics);
    logger.debug('DASHBOARD', 'UserDashboard dashboardData:', dashboardData);
  }
  
  return (
    <div className="user-dashboard">
      {/* Personal Stats */}
      <div className="dashboard-grid overview-grid">
        <div className="stat-card bookings-card">
          <div className="stat-icon">
            <CalendarCheck size={24} />
          </div>
          <div className="stat-content">
            <h3>Total Bookings</h3>
            <div className="stat-number">
              {userAnalytics?.booking_analytics?.total_bookings || 0}
            </div>
            <div className="stat-meta">
              <span className="stat-trend positive">
                {userAnalytics?.booking_analytics?.upcoming_bookings || 0} upcoming
              </span>
            </div>
          </div>
        </div>

        <div className="stat-card success-card">
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <h3>Completed Bookings</h3>
            <div className="stat-number">
              {userAnalytics?.booking_analytics?.past_bookings || 0}
            </div>
            <div className="stat-meta">
              <span className="stat-trend">
                {userAnalytics?.booking_analytics?.cancelled_bookings || 0} cancelled
              </span>
            </div>
          </div>
        </div>

        <div className="stat-card messages-card">
          <div className="stat-icon">
            <MessageSquare size={24} />
          </div>
          <div className="stat-content">
            <h3>Messages Sent</h3>
            <div className="stat-number">
              {userAnalytics?.communication_analytics?.total_messages || 0}
            </div>
            <div className="stat-meta">
              <span className="stat-trend">
                {userAnalytics?.communication_analytics?.unique_sessions || 0} sessions
              </span>
            </div>
          </div>
        </div>

        {/* Response Time section commented out for now
        <div className="stat-card value-card">
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <h3>Response Time</h3>
            <div className="stat-number">
              {userAnalytics?.communication_analytics?.average_response_time || 'N/A'}
            </div>
            <div className="stat-meta">
              <span className="stat-trend">
                Average chat response
              </span>
            </div>
          </div>
        </div>
        */}

        {/* Coin Statistics */}
        <CoinStatistics />
      </div>

      {/* Recent Activity */}
      <div className="dashboard-grid content-grid">
        {/* Recent Bookings */}
        <div className="analytics-card recent-bookings-card compact-card">
          <div className="card-header">
            <h3>Recent Bookings</h3>
            <Calendar size={20} />
          </div>
          <div className="bookings-scroll-container">
            {dashboardData?.recent_bookings?.length > 0 ? (
              <div className="bookings-scroll-list">
                {dashboardData.recent_bookings.map((booking) => (
                  <div key={`${booking.resource_name}-${booking.start_datetime}`} className="booking-compact-item">
                    <div className="booking-compact-left">
                      <div className="booking-icon-small">
                        <MapPin size={12} />
                      </div>
                    </div>
                    <div className="booking-compact-content">
                      <div className="booking-title">{booking.resource_name}</div>
                      <div className="booking-meta">
                        <span className="booking-loc">{booking.location_name}</span>
                        <span className="booking-time">{format(new Date(booking.start_datetime), 'MMM d, HH:mm')}</span>
                      </div>
                    </div>
                    <div className={`booking-chip status-${booking.status}`}>
                      {booking.status}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-bookings-empty">
                <Calendar size={32} />
                <p>No recent bookings</p>
              </div>
            )}
          </div>
        </div>

        {/* Preferences */}
        <div className="analytics-card preferences-card">
          <div className="card-header">
            <h3>Your Preferences</h3>
            <TrendingUp size={20} />
          </div>
          <div className="preferences-list">
            <div className="preference-item">
              <div className="preference-label">
                <MapPin size={16} style={{ marginRight: '0.5rem', color: 'var(--primary-color, #3b82f6)' }} />
                Preferred Location
              </div>
              <div className="preference-value">
                {userAnalytics?.booking_analytics?.calculated_preferences?.preferred_location || 
                 userAnalytics?.booking_analytics?.favorite_locations?.[0]?.location_name ||
                 (userAnalytics?.booking_analytics?.total_bookings === 0 ? 'No bookings yet' : 'N/A')}
              </div>
              {userAnalytics?.booking_analytics?.calculated_preferences?.location_counts && 
               Object.keys(userAnalytics.booking_analytics.calculated_preferences.location_counts).length > 0 && (
                <div className="preference-count">
                  {Object.entries(userAnalytics.booking_analytics.calculated_preferences.location_counts)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 1)
                    .map(([location, count]) => `${count} bookings at this location`)
                  }
                </div>
              )}
            </div>
            
            <div className="preference-item">
              <div className="preference-label">
                <Activity size={16} style={{ marginRight: '0.5rem', color: 'var(--primary-color, #3b82f6)' }} />
                Preferred Resource Type
              </div>
              <div className="preference-value">
                {userAnalytics?.booking_analytics?.calculated_preferences?.preferred_resource_type?.replace(/_/g, ' ') ||
                 (userAnalytics?.booking_analytics?.total_bookings === 0 ? 'No bookings yet' : 'N/A')}
              </div>
              {userAnalytics?.booking_analytics?.calculated_preferences?.resource_type_counts && 
               Object.keys(userAnalytics.booking_analytics.calculated_preferences.resource_type_counts).length > 0 && (
                <div className="preference-count">
                  {Object.entries(userAnalytics.booking_analytics.calculated_preferences.resource_type_counts)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 1)
                    .map(([type, count]) => `${count} bookings of this type`)
                  }
                </div>
              )}
            </div>
            
            <div className="preference-item">
              <div className="preference-label">
                <CheckCircle size={16} style={{ marginRight: '0.5rem', color: 'var(--success-color, #10b981)' }} />
                Favorite Resource
              </div>
              <div className="preference-value">
                {userAnalytics?.booking_analytics?.calculated_preferences?.preferred_resource_name ||
                 userAnalytics?.booking_analytics?.favorite_resources?.[0]?.resource_name ||
                 (userAnalytics?.booking_analytics?.total_bookings === 0 ? 'No bookings yet' : 'N/A')}
              </div>
              {userAnalytics?.booking_analytics?.calculated_preferences?.resource_name_counts && 
               Object.keys(userAnalytics.booking_analytics.calculated_preferences.resource_name_counts).length > 0 && (
                <div className="preference-count">
                  {Object.entries(userAnalytics.booking_analytics.calculated_preferences.resource_name_counts)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 1)
                    .map(([name, count]) => `${count} times booked`)
                  }
                </div>
              )}
            </div>
            
            <div className="preference-item">
              <div className="preference-label">
                <Calendar size={16} style={{ marginRight: '0.5rem', color: 'var(--warning-color, #f59e0b)' }} />
                Most Active Day
              </div>
              <div className="preference-value">
                {userAnalytics?.booking_analytics?.calculated_preferences?.most_active_day ||
                 userAnalytics?.booking_analytics?.booking_trends?.most_active_day ||
                 (userAnalytics?.booking_analytics?.total_bookings === 0 ? 'No bookings yet' : 'N/A')}
              </div>
            </div>
            
            <div className="preference-item">
              <div className="preference-label">
                <Clock size={16} style={{ marginRight: '0.5rem', color: 'var(--info-color, #8b5cf6)' }} />
                Preferred Time
              </div>
              <div className="preference-value">
                {userAnalytics?.booking_analytics?.calculated_preferences?.most_active_time ||
                 userAnalytics?.booking_analytics?.booking_trends?.most_active_time || 
                 (userAnalytics?.booking_analytics?.total_bookings === 0 ? 'No bookings yet' : 'N/A')}
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Summary */}
        <div className="analytics-card summary-card">
          <div className="card-header">
            <h3>This Month</h3>
            <Clock size={20} />
          </div>
          <div className="summary-stats">
            <div className="summary-item">
              <div className="summary-number">
                {dashboardData?.monthly_summary?.total_bookings || 0}
              </div>
              <div className="summary-label">Total Bookings</div>
            </div>
            <div className="summary-item">
              <div className="summary-number">
                {dashboardData?.monthly_summary?.confirmed_bookings || 0}
              </div>
              <div className="summary-label">Confirmed</div>
            </div>
            <div className="summary-item">
              <div className="summary-number">
                {dashboardData?.monthly_summary?.cancelled_bookings || 0}
              </div>
              <div className="summary-label">Cancelled</div>
            </div>
          </div>
        </div>
      </div>

      {/* Grant chart is hide for normal user fronte 840 to 851 */}

      {/* Gantt Chart Timeline for User - Hidden as requested */}
      {/* <div className="dashboard-grid gantt-section">
        <div className="gantt-wrapper">
          <Suspense fallback={
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading timeline...
            </div>
          }>
            <GanttChartV2 autoRefresh={false} />
          </Suspense>
        </div>
      </div> */}
    </div>
  );
};

export default Dashboard;